import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const LEADER_TIMEOUT_MS = 1000;

const isTextInput = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

// Vim-style "g <key>" leader sequences plus "?" for help. Routes:
//   g h  → /
//   g a  → /apps
//   ?    → opens the help overlay (caller-provided handler)
export const useGoShortcuts = (onHelp: () => void) => {
  const navigate = useNavigate();
  const leaderTimer = useRef<number | null>(null);

  useEffect(() => {
    const clearLeader = () => {
      if (leaderTimer.current !== null) {
        window.clearTimeout(leaderTimer.current);
        leaderTimer.current = null;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (isTextInput(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (leaderTimer.current !== null) {
        const k = e.key.toLowerCase();
        clearLeader();
        if (k === 'h') {
          e.preventDefault();
          navigate('/');
        } else if (k === 'a') {
          e.preventDefault();
          navigate('/apps');
        }
        return;
      }

      if (e.key === '?') {
        e.preventDefault();
        onHelp();
        return;
      }

      if (e.key === 'g') {
        leaderTimer.current = window.setTimeout(clearLeader, LEADER_TIMEOUT_MS);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearLeader();
    };
  }, [navigate, onHelp]);
};
